/*============================================================================
  ArtMind Sofortsuche (ersetzt xcloud Search)

  Nutzt Shopifys native Vorschlagssuche (/search/suggest.json):
  - Tippfehler-tolerant (z.B. "flamngo", "bansky", "leinwnad")
  - Synonymgruppen aus der Shopify-App "Search & Discovery" (Admin -> Apps ->
    Search & Discovery -> Synonyme) wirken automatisch mit
  Ein Request pro Eingabepause statt pro Tastendruck, veraltete Anfragen werden
  abgebrochen, Begriffe gecacht.

  Markup: [data-am-search] mit einem form > input[name="q"] darin. Texte kommen aus
  data-Attributen (Liquid-Uebersetzungen), siehe sections/header.liquid.
==============================================================================*/
(function() {
  const DEBOUNCE = 140;
  const MIN_CHARS = 2;
  const mobileQuery = window.matchMedia('(max-width: 768px)');
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  let uid = 0;

  const escapeHtml = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const normalize = (s) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ß/g, 'ss');

  // Editierdistanz, damit auch bei Tippfehlern das passende Wort im Titel
  // hervorgehoben wird ("flamngo" -> <mark>Flamingo</mark> Afternoon).
  // Damerau-Levenshtein (Buchstabendreher wie "bansky" zaehlen als ein Fehler)
  function distance(a, b) {
    const d = [];
    for (let i = 0; i <= a.length; i++) d[i] = [i];
    for (let j = 0; j <= b.length; j++) d[0][j] = j;
    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
    return d[a.length][b.length];
  }

  function closeEnough(word, token) {
    if (word.startsWith(token)) return true;
    if (token.length < 4) return false;
    const max = token.length > 6 ? 2 : 1;
    return distance(word, token) <= max || distance(word.slice(0, token.length), token) <= max;
  }

  function highlight(title, terms) {
    const tokens = normalize(terms).split(/\s+/).filter((t) => t.length >= 2);
    if (!tokens.length) return escapeHtml(title);
    return title.split(/(\s+)/).map((part) => {
      const n = normalize(part).replace(/[^a-z0-9]/g, '');
      const hit = n.length >= 2 && tokens.some((t) => closeEnough(n, t));
      return hit ? '<mark>' + escapeHtml(part) + '</mark>' : escapeHtml(part);
    }).join('');
  }

  function imageUrl(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function money(amount) {
    const cents = Math.round(parseFloat(amount) * 100);
    if (window.theme && theme.Currency && theme.settings) return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    return (cents / 100).toFixed(2);
  }

  class AmSearch {
    constructor(el) {
      this.el = el;
      this.form = el.querySelector('form');
      this.input = el.querySelector('input[name="q"]');
      if (!this.form || !this.input) return;

      this.labels = el.dataset;
      this.cache = new Map();
      this.active = -1;
      this.term = '';
      this.id = 'am-search-' + (++uid);

      this.panel = document.createElement('div');
      this.panel.className = 'am-search__panel';
      this.panel.id = this.id;
      this.panel.hidden = true;
      this.panel.setAttribute('role', 'listbox');
      this.panel.setAttribute('aria-label', this.labels.labelProducts || 'Produkte');

      this.backdrop = document.createElement('div');
      this.backdrop.className = 'am-search__backdrop';
      this.backdrop.hidden = true;

      this.cancel = document.createElement('button');
      this.cancel.type = 'button';
      this.cancel.className = 'am-search__cancel';
      this.cancel.textContent = this.labels.labelCancel || 'Abbrechen';
      this.input.parentNode.appendChild(this.cancel);

      Object.assign(this.input, { autocomplete: 'off' });
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-controls', this.id);
      this.input.removeAttribute('aria-owns');

      this.onInput = this.debounce(() => this.search(this.input.value), DEBOUNCE);
      this.input.addEventListener('input', () => {
        if (this.input.value.trim().length < MIN_CHARS) { this.abort(); this.renderEmpty(); return; }
        this.onInput();
      });
      this.input.addEventListener('focus', () => this.open());
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));

      // Klicks im Panel sollen den Fokus nicht vom Feld nehmen (sonst klappt das
      // Desktop-Suchfeld beim mousedown zusammen und der Klick geht ins Leere)
      this.panel.addEventListener('mousedown', (e) => e.preventDefault());
      this.panel.addEventListener('click', (e) => {
        const all = e.target.closest('[data-am-search-all]');
        if (all) { e.preventDefault(); this.submit(); }
      });
      this.cancel.addEventListener('click', () => { this.input.value = ''; this.renderEmpty(); this.close(); this.input.blur(); });
      this.backdrop.addEventListener('click', () => this.close());
      document.addEventListener('pointerdown', (e) => {
        if (!this.isOpen || this.el.contains(e.target) || this.panel.contains(e.target)) return;
        this.close();
      }, { passive: true });
      this.form.addEventListener('submit', () => this.close());
      window.addEventListener('pageshow', () => this.close());
    }

    debounce(fn, wait) {
      let t;
      return () => { clearTimeout(t); t = setTimeout(fn, wait); };
    }

    get isMobile() { return mobileQuery.matches; }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      // Mobil als Vollbild-Overlay im <body> (der Header hat backdrop-filter und waere
      // sonst der Bezugsrahmen fuer position: fixed), Desktop als Dropdown am Feld
      const host = this.isMobile ? document.body : this.el;
      if (this.panel.parentNode !== host) host.appendChild(this.panel);
      if (this.isMobile) {
        if (!this.backdrop.isConnected) document.body.appendChild(this.backdrop);
        this.backdrop.hidden = false;
        this.el.classList.add('am-search--open');
        document.documentElement.classList.add('am-search-lock');
        this.positionMobile();
        this.onViewport = () => this.positionMobile();
        if (window.visualViewport) visualViewport.addEventListener('resize', this.onViewport);
      }
      if (this.input.value.trim().length >= MIN_CHARS) this.search(this.input.value);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.abort();
      this.panel.hidden = true;
      this.backdrop.hidden = true;
      this.el.classList.remove('am-search--open');
      document.documentElement.classList.remove('am-search-lock');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      if (this.onViewport && window.visualViewport) visualViewport.removeEventListener('resize', this.onViewport);
    }

    positionMobile() {
      const top = Math.round(this.el.getBoundingClientRect().bottom) + 8;
      const vh = window.visualViewport ? visualViewport.height : window.innerHeight;
      this.panel.style.top = this.backdrop.style.top = top + 'px';
      this.panel.style.maxHeight = Math.max(160, vh - top) + 'px';
    }

    abort() {
      clearTimeout(this.pending);
      if (this.controller) this.controller.abort();
      this.controller = null;
    }

    search(raw) {
      const term = raw.trim();
      if (term.length < MIN_CHARS) return;
      this.term = term;
      const key = term.toLowerCase();
      if (this.cache.has(key)) { this.abort(); this.render(this.cache.get(key), term); return; }

      this.abort();
      const controller = this.controller = new AbortController();
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,collection',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,tag'
      });
      this.el.classList.add('am-search--loading');
      fetch(root + 'search/suggest.json?' + params, { signal: controller.signal, headers: { Accept: 'application/json' } })
        .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then((data) => {
          const results = (data.resources && data.resources.results) || {};
          this.cache.set(key, results);
          if (controller === this.controller) this.render(results, term);
        })
        .catch((err) => { if (err.name !== 'AbortError') this.renderEmpty(); })
        .finally(() => { if (controller === this.controller) this.el.classList.remove('am-search--loading'); });
    }

    render(results, term) {
      if (!this.isOpen) return;
      const products = results.products || [];
      const collections = (results.collections || []).slice(0, 4);
      const L = this.labels;
      let html = '';

      if (collections.length) {
        html += '<div class="am-search__group"><p class="am-search__heading">' + escapeHtml(L.labelCollections || 'Kollektionen') + '</p><div class="am-search__chips">' +
          collections.map((c, i) => '<a class="am-search__chip" role="option" id="' + this.id + '-c' + i + '" href="' + escapeHtml(c.url) + '">' + escapeHtml(c.title) + '</a>').join('') +
          '</div></div>';
      }

      if (products.length) {
        html += '<div class="am-search__group"><p class="am-search__heading">' + escapeHtml(L.labelProducts || 'Produkte') + '</p><ul class="am-search__list">' +
          products.map((p, i) => {
            const img = p.image || (p.featured_image && p.featured_image.url);
            const alt = (p.featured_image && p.featured_image.alt) || '';
            let price = money(p.price_min || p.price);
            if (p.price_min !== p.price_max && L.labelFrom) price = L.labelFrom.replace('__PRICE__', price);
            return '<li><a class="am-search__item" role="option" id="' + this.id + '-p' + i + '" href="' + escapeHtml(p.url) + '">' +
              '<span class="am-search__thumb">' + (img ? '<img src="' + escapeHtml(imageUrl(img, 120)) + '" srcset="' + escapeHtml(imageUrl(img, 120)) + ' 1x, ' + escapeHtml(imageUrl(img, 240)) + ' 2x" width="60" height="60" alt="' + escapeHtml(alt) + '" loading="eager" decoding="async">' : '') + '</span>' +
              '<span class="am-search__info"><span class="am-search__title">' + highlight(p.title, term) + '</span>' +
              (p.vendor ? '<span class="am-search__vendor">' + escapeHtml(p.vendor) + '</span>' : '') + '</span>' +
              '<span class="am-search__price">' + price + '</span></a></li>';
          }).join('') + '</ul></div>';
      }

      if (products.length || collections.length) {
        html += '<a class="am-search__all" role="option" id="' + this.id + '-all" href="#" data-am-search-all>' +
          escapeHtml((L.labelViewAll || 'Alle Ergebnisse für "__TERMS__" anzeigen').replace('__TERMS__', term)) + '</a>';
      } else {
        html += '<a class="am-search__empty" role="option" id="' + this.id + '-all" href="#" data-am-search-all>' + escapeHtml((L.labelNoResults || 'Suche nach "__TERMS__"').replace('__TERMS__', term)) + '</a>';
      }

      this.panel.innerHTML = html;
      this.options = Array.from(this.panel.querySelectorAll('[role="option"]'));
      this.setActive(-1);
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    renderEmpty() {
      this.panel.hidden = true;
      this.panel.innerHTML = '';
      this.options = [];
      this.input.setAttribute('aria-expanded', 'false');
    }

    setActive(index) {
      const opts = this.options || [];
      if (this.active > -1 && opts[this.active]) opts[this.active].classList.remove('is-active');
      this.active = index;
      if (index > -1 && opts[index]) {
        opts[index].classList.add('is-active');
        opts[index].scrollIntoView({ block: 'nearest' });
        this.input.setAttribute('aria-activedescendant', opts[index].id);
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    onKeydown(e) {
      const opts = this.options || [];
      if (e.key === 'Escape') { this.close(); return; }
      if (this.panel.hidden || !opts.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); this.setActive((this.active + 1) % opts.length); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); this.setActive(this.active <= 0 ? opts.length - 1 : this.active - 1); }
      else if (e.key === 'Enter' && this.active > -1) { e.preventDefault(); opts[this.active].click(); }
    }

    submit() {
      if (this.form.requestSubmit) this.form.requestSubmit(); else this.form.submit();
    }
  }

  document.querySelectorAll('[data-am-search]').forEach((el) => { if (!el._amSearch) el._amSearch = new AmSearch(el); });
})();
